import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  getSubscription,
    updateSubscriptionPlan,
    createPaymentOrder,
    verifyPayment,
    cancelDowngrade,
    getPublicPricing,
    getActiveCoupons,
} from '../utils/api';
import { trackEvent } from '../utils/analytics';

const plans = [
  {
    id: 'free',
    name: 'Free',
    features: [
      '1 client',
      'Manual data entry',
      'Basic dashboard',
      '1 report/month',
    ],
  },
  {
    id: 'payperuse',
    name: 'Pay Per Report',
    features: [
      '1 client',
      '__PRICE_PER_REPORT__',
      'No monthly limit on reports',
      'Standard report format',
      'Includes "Unbrand" watermark',
    ],
  },
  {
    id: 'pro',
    name: 'Pro',
    features: [
      '__PRO_CLIENTS__',
      '__PRO_REPORTS__',
      'Excel/CSV upload',
      'Professional PDF export',
      'White-label PDF',
      'AI insights',
      'Report history',
    ],
  },
  {
    id: 'agency',
    name: 'Agency',
    features: [
      'Unlimited clients & reports',
      'Everything in Pro',
      'Multiple team members',
      'Multiple client workspaces',
      'Custom branding & logo',
      'Advanced analytics',
      'Priority support',
    ],
  },
];

const loadRazorpayScript = () => {
  return new Promise((resolve) => {
    if (window.Razorpay) {
      resolve(true);
      return;
    }

    const script = document.createElement('script');
    script.src = 'https://checkout.razorpay.com/v1/checkout.js';
    script.onload = () => resolve(true);
    script.onerror = () => resolve(false);
    document.body.appendChild(script);
  });
};

export default function Subscription() {
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [processingPlan, setProcessingPlan] = useState(null);
  const [paymentSuccess, setPaymentSuccess] = useState(null);
  const [billingCycle, setBillingCycle] = useState('monthly');
  const [pricing, setPricing] = useState({});
  const [couponCode, setCouponCode] = useState('');
  const [coupons, setCoupons] = useState([]);

  const loadSubscription = async () => {
    try {
      const data = await getSubscription();
      setSubscription(data);
    } catch {
      toast.error('Failed to load subscription');
    } finally {
      setLoading(false);
    }
  };

  const loadPricing = async () => {
    try {
      const plansData = await getPublicPricing();
      setPricing(Object.fromEntries(plansData.map((p) => [p.plan_key, p])));
    } catch {
      // Non-fatal — the checkout still works, prices just won't show until this loads
    }
  };

  const loadCoupons = async () => {
    try {
      const data = await getActiveCoupons();
      setCoupons(data || []);
    } catch {
      // Non-fatal — the coupon input still works without the offers list.
    }
  };

  useEffect(() => {
    loadSubscription();
    loadPricing();
    loadCoupons();
  }, []);

  const couponLabel = (coupon) => {
    const discount = coupon.discount_type === 'percent'
      ? `${Number(coupon.discount_value)}% off`
      : `₹${Number(coupon.discount_value).toLocaleString('en-IN')} off`;
    const scope = coupon.applies_to_plan
      ? `${coupon.applies_to_plan.charAt(0).toUpperCase()}${coupon.applies_to_plan.slice(1)} plan`
      : 'any plan';
    return `${discount} · ${scope}`;
  };

  const handleUpgrade = async (planName) => {
    try {
      setProcessingPlan(planName);

      if (planName === 'free' || planName === 'payperuse') {
        const updated = await updateSubscriptionPlan(planName);
        setSubscription(updated);
      trackEvent('plan_changed', { plan_name: planName });
      toast.success(
         updated.downgrade_scheduled
           ? 'Downgrade scheduled after current billing period'
           : `Plan changed to ${planName === 'payperuse' ? 'Pay Per Report' : 'Free'}`
       );
        return;
      }

      const scriptLoaded = await loadRazorpayScript();

      if (!scriptLoaded) {
        toast.error('Payment gateway failed to load');
        return;
      }

      const orderData = await createPaymentOrder({
        planName,
        billingCycle,
        couponCode: couponCode.trim() || undefined,
      });

      if (orderData.discountAmount > 0) {
        toast.success(`Coupon applied — you saved ₹${orderData.discountAmount.toLocaleString('en-IN')}`);
      }

      const options = {
        key: orderData.key,
        amount: orderData.order.amount,
        currency: orderData.order.currency,
        name: 'Unbrand',
        description: `${planName.toUpperCase()} Plan Subscription`,
        order_id: orderData.order.id,

        handler: async function (response) {
          try {
            await verifyPayment({
              razorpay_order_id: response.razorpay_order_id,
              razorpay_payment_id: response.razorpay_payment_id,
              razorpay_signature: response.razorpay_signature,
              planName,
             billingCycle,
            });

           await loadSubscription();

           setPaymentSuccess({
             planName,
             paymentId: response.razorpay_payment_id,
             orderId: response.razorpay_order_id,
           });

           trackEvent('purchase', {
             transaction_id: response.razorpay_payment_id,
             value: orderData.order.amount / 100,
             currency: orderData.order.currency,
             plan_name: planName,
             billing_cycle: billingCycle,
           });
           toast.success(`Successfully upgraded to ${planName}`);
          } catch {
            toast.error('Payment verification failed');
          }
        },

        theme: {
          color: '#2563EB',
        },
      };

      const razorpay = new window.Razorpay(options);

      razorpay.on('payment.failed', function () {
        toast.error('Payment failed or cancelled');
      });

      razorpay.open();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Failed to start payment');
    } finally {
      setProcessingPlan(null);
    }
  };

const handleCancelDowngrade = async () => {
  try {
    setProcessingPlan('cancel-downgrade');

    const updated = await cancelDowngrade();

    setSubscription(updated);
    await loadSubscription();

    toast.success('Downgrade cancelled.');
  } catch (error) {
    toast.error(error.response?.data?.error || 'Failed to cancel downgrade');
  } finally {
    setProcessingPlan(null);
  }
};

const getPlanPrice = (planId) => {
  const plan = pricing[planId];
  if (!plan) return '...';
  if (planId === 'free') return '₹0';
  if (planId === 'payperuse') return `₹${Number(plan.monthly_price || 0).toLocaleString('en-IN')}/report`;

  const price = billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price;
  const suffix = billingCycle === 'yearly' ? '/year' : '/month';
  return `₹${Number(price || 0).toLocaleString('en-IN')}${suffix}`;
};

// Client-side preview only — mirrors the same math resolveCoupon() does
// server-side in payments.js, so the price shown here matches what
// Razorpay checkout will actually charge. The server remains the real
// source of truth (it re-validates the code at order-creation time); this
// is purely so clicking a coupon chip has a visible effect on the page
// instead of only showing up after opening the Razorpay popup.
const getCouponForPlan = (planId) => {
  const code = couponCode.trim().toUpperCase();
  if (!code) return null;
  return coupons.find(
    (c) => c.code === code && (!c.applies_to_plan || c.applies_to_plan === planId)
  ) || null;
};

const getDiscountedPrice = (planId) => {
  const plan = pricing[planId];
  const coupon = getCouponForPlan(planId);
  if (!plan || planId === 'free' || planId === 'payperuse' || !coupon) return null;

  const price = Number((billingCycle === 'yearly' ? plan.yearly_price : plan.monthly_price) || 0);
  const discount = coupon.discount_type === 'percent'
    ? Math.round((price * Number(coupon.discount_value)) / 100)
    : Math.round(Number(coupon.discount_value));
  const finalPrice = Math.max(0, price - Math.min(discount, price));
  const suffix = billingCycle === 'yearly' ? '/year' : '/month';
  return `₹${finalPrice.toLocaleString('en-IN')}${suffix}`;
};

  return (
    <div className="fade-in">
      <div style={{ marginBottom: 26 }}>
        <div className="page-title">Subscription</div>
        <div className="page-subtitle">
          Manage your current plan and upgrade features.
        </div>
        {subscription && (
          <div
            className="card card-pad"
            style={{
              marginTop: 18,
              background: 'linear-gradient(135deg,#EEF2FF,#F8FAFC)',
              border: '1px solid #C7D2FE',
            }}
          >
            <div style={{ fontSize: 12, color: 'var(--text3)', marginBottom: 6 }}>
              Current Subscription
            </div>

            <div style={{ fontSize: 20, fontWeight: 900, textTransform: 'capitalize' }}>
              {subscription.plan_name} Plan
            </div>

            <div style={{ marginTop: 8, fontSize: 13, color: 'var(--text2)' }}>
              Status: <strong>{subscription.status}</strong>
            </div>

            <div style={{ marginTop: 4, fontSize: 13, color: 'var(--text2)' }}>
              Next billing date:{' '}
              <strong>
                {subscription.expires_at
                  ? new Date(subscription.expires_at).toLocaleDateString()
                  : 'Not applicable'}
              </strong>
            </div>
            {subscription?.downgrade_scheduled && (
              <div
                style={{
                  marginTop: 14,
                  padding: 12,
                  borderRadius: 12,
                  background: '#FFF7ED',
                  border: '1px solid #FDBA74',
                }}
              >
                <div style={{ fontSize: 13, fontWeight: 800, color: '#C2410C' }}>
                  Downgrade scheduled
                </div>

                <div style={{ marginTop: 4, fontSize: 12, color: 'var(--text2)' }}>
                 Your plan will change to {subscription.next_plan_name?.toUpperCase()} on{' '}
                 {subscription.expires_at
                   ? new Date(subscription.expires_at).toLocaleDateString()
                   : 'billing end'}.
                 Until then, your current plan remains active.
                </div>

                <button
                  className="btn btn-primary"
                  style={{ marginTop: 10 }}
                  onClick={handleCancelDowngrade}
                 disabled={processingPlan !== null}
                >
                {processingPlan === 'cancel-downgrade'
                  ? 'Processing...'
                  : `Keep ${subscription.plan_name?.toUpperCase()} Active`}
                </button>
              </div>
            )}
          </div>
        )}
      </div>

{paymentSuccess && (
  <div
    className="card card-pad"
    style={{
      marginBottom: 22,
      border: '1px solid #A7F3D0',
      background: 'linear-gradient(135deg,#ECFDF5,#F8FAFC)',
    }}
  >
    <div
      style={{
        fontWeight: 900,
        fontSize: 18,
        color: '#059669',
        marginBottom: 6,
      }}
    >
      Payment Successful
    </div>

    <div
      style={{
        color: 'var(--text2)',
        fontSize: 13,
        marginBottom: 12,
      }}
    >
      Your {paymentSuccess.planName.toUpperCase()} plan has been activated.
    </div>

    <div style={{ fontSize: 12, color: 'var(--text3)' }}>
      Payment ID: {paymentSuccess.paymentId}
    </div>

    <div
      style={{
        fontSize: 12,
        color: 'var(--text3)',
        marginTop: 4,
      }}
    >
      Order ID: {paymentSuccess.orderId}
    </div>

    {process.env.REACT_APP_ENABLE_PLATFORM_SYNC === 'true' && (
      <div
        style={{
          marginTop: 16,
          paddingTop: 16,
          borderTop: '1px solid #A7F3D0',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
          flexWrap: 'wrap',
        }}
      >
        <div style={{ fontSize: 13, color: 'var(--text2)' }}>
          <strong style={{ color: 'var(--text)' }}>Next step:</strong> connect your clients'
          Meta, Google or LinkedIn Ads accounts so their reports sync automatically.
        </div>
        <Link to="/integrations" className="btn btn-primary btn-sm" style={{ textDecoration: 'none', whiteSpace: 'nowrap' }}>
          Connect an account
        </Link>
      </div>
    )}
  </div>
)}
<div
  style={{
    display: 'flex',
    gap: 8,
    marginBottom: 22,
    background: 'var(--bg3)',
    padding: 5,
    borderRadius: 12,
    width: 'fit-content',
  }}
>
  {['monthly', 'yearly'].map((cycle) => (
    <button
      key={cycle}
      type="button"
      onClick={() => setBillingCycle(cycle)}
      style={{
        padding: '8px 18px',
        borderRadius: 9,
        border: 'none',
        cursor: 'pointer',
        fontSize: 13,
        fontWeight: 800,
        textTransform: 'capitalize',
        background: billingCycle === cycle ? 'var(--bg2)' : 'transparent',
        color: billingCycle === cycle ? 'var(--text)' : 'var(--text2)',
        boxShadow: billingCycle === cycle ? 'var(--shadow)' : 'none',
      }}
    >
      <>
        {cycle}

        {cycle === 'yearly' && (
          <span
            style={{
              marginLeft: 6,
              fontSize: 10,
              background: '#DCFCE7',
              color: '#166534',
              padding: '2px 6px',
              borderRadius: 999,
              fontWeight: 800,
            }}
          >
            SAVE 25%
          </span>
        )}
      </>
    </button>
  ))}
</div>

{coupons.length > 0 && (
  <div style={{ marginBottom: 18 }}>
    <div style={{ fontSize: 12, fontWeight: 700, color: 'var(--text3)', marginBottom: 8 }}>
      Available offers
    </div>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
      {coupons.map((coupon) => (
        <button
          key={coupon.code}
          type="button"
          onClick={() => setCouponCode(coupon.code)}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            padding: '8px 14px',
            borderRadius: 999,
            cursor: 'pointer',
            fontSize: 12,
            fontWeight: 700,
            border: couponCode === coupon.code ? '1.5px solid #2563EB' : '1px solid var(--border)',
            background: couponCode === coupon.code ? '#EFF6FF' : 'var(--bg2)',
            color: couponCode === coupon.code ? '#1D4ED8' : 'var(--text)',
          }}
        >
          <span style={{ fontFamily: 'var(--mono)' }}>{coupon.code}</span>
          <span style={{ fontWeight: 500, color: 'var(--text3)' }}>{couponLabel(coupon)}</span>
        </button>
      ))}
    </div>
  </div>
)}

<div className="form-group" style={{ maxWidth: 280, marginBottom: 22 }}>
  <label className="form-label">Have a coupon code?</label>
  <input
    className="form-input"
    placeholder="e.g. LAUNCH50"
    value={couponCode}
    onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
  />
  {couponCode.trim() && (
    coupons.some((c) => c.code === couponCode.trim().toUpperCase()) ? (
      <div style={{ fontSize: 11, color: '#059669', fontWeight: 700, marginTop: 6 }}>
        ✓ Valid — discount shown below on eligible plans
      </div>
    ) : (
      <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
        Not a recognized code yet — it'll be checked when you upgrade
      </div>
    )
  )}
</div>

      {loading ? (
        <div className="card card-pad">Loading subscription...</div>
      ) : (
        <div className="grid grid-4" style={{ gap: 20 }}>
          {plans.map((plan) => {
            const active = subscription?.plan_name === plan.id;
            const processing = processingPlan === plan.id;

            const downgradeToThisPlan =
              subscription?.downgrade_scheduled &&
              subscription?.next_plan_name === plan.id;

            return (
              <div
                key={plan.id}
                className="card card-pad"
                style={{
                  border: active ? '2px solid #2563EB' : '1px solid var(--border)',
                  position: 'relative',
                }}
              >
                {active && (
                  <div
                    style={{
                      position: 'absolute',
                      top: 14,
                      right: 14,
                      background: '#DBEAFE',
                      color: '#2563EB',
                      padding: '4px 10px',
                      borderRadius: 999,
                      fontSize: 11,
                      fontWeight: 800,
                    }}
                  >
                    CURRENT
                  </div>
                )}

                <div style={{ fontSize: 22, fontWeight: 800, marginBottom: 8 }}>
                  {plan.name}
                </div>

                <div style={{ marginBottom: 18 }}>
                  {getDiscountedPrice(plan.id) ? (
                    <div style={{ display: 'flex', alignItems: 'baseline', gap: 8, flexWrap: 'wrap' }}>
                      <span style={{ fontSize: 28, fontWeight: 900, color: '#059669' }}>
                        {getDiscountedPrice(plan.id)}
                      </span>
                      <span style={{ fontSize: 15, fontWeight: 700, color: 'var(--text3)', textDecoration: 'line-through' }}>
                        {getPlanPrice(plan.id)}
                      </span>
                      <span style={{ fontSize: 10, fontWeight: 800, color: '#059669', background: '#DCFCE7', padding: '2px 8px', borderRadius: 999, textTransform: 'uppercase' }}>
                        {couponCode.trim().toUpperCase()} applied
                      </span>
                    </div>
                  ) : (
                    <div style={{ fontSize: 28, fontWeight: 900 }}>
                      {getPlanPrice(plan.id)}
                    </div>
                  )}
                </div>

                <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 24 }}>
                  {plan.features.map((feature) => (
                    <div key={feature} style={{ color: 'var(--text2)', fontSize: 14 }}>
                      ✅ {feature === '__PRICE_PER_REPORT__'
                        ? `₹${Number(pricing.payperuse?.monthly_price ?? 9).toLocaleString('en-IN')} per report generated`
                        : feature === '__PRO_CLIENTS__'
                        ? (!pricing.pro ? '...' : pricing.pro.client_limit == null ? 'Unlimited clients' : `Up to ${pricing.pro.client_limit} clients`)
                        : feature === '__PRO_REPORTS__'
                        ? (!pricing.pro ? '...' : pricing.pro.report_limit == null ? 'Unlimited reports' : `${pricing.pro.report_limit} reports/month`)
                        : feature}
                    </div>
                  ))}
                </div>

                <button
                  className={active ? 'btn btn-secondary' : 'btn btn-primary'}
                  style={{ width: '100%' }}
                disabled={active || processingPlan !== null || downgradeToThisPlan}
                  onClick={() => handleUpgrade(plan.id)}
                >
               {downgradeToThisPlan
                 ? `Will switch on ${
                     subscription?.expires_at
                       ? new Date(subscription.expires_at).toLocaleDateString()
                       : 'billing end'
                   }`
                 : processing
                 ? 'Processing...'
                 : plan.id === 'free'
                 ? 'Switch to Free'
                 : plan.id === 'payperuse'
                 ? 'Switch to Pay Per Report'
                 : `Pay & Choose ${plan.name}`}
                </button>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
